import { useEffect, useRef, useState } from "preact/hooks";
import {
  ACTION_GROUPS,
  ACTION_LABELS,
  MAX_BINDINGS_PER_ACTION,
  bindingLabel,
  clearBinding,
  duplicateBindings,
  findConflicts,
  isActionDefault,
  isWheelBinding,
  resetAction,
  setBinding,
  settings,
  updateSettings,
} from "../../../settings";
import type { Action, Binding } from "../../../settings";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Visualizer } from "../../components/Visualizer";
import { cx } from "../../cx";
import { useEscape } from "../../hooks/hooks";
import { CAPTURE_PROMPT, KeybindCapture } from "./KeybindCapture";

interface Target {
  action: Action;
  slot: number;
}

/** A captured binding that other actions already use, waiting for the player's decision. */
interface PendingConflict extends Target {
  binding: Binding;
  conflicts: Action[];
}

const SLOTS: readonly number[] = Array.from({ length: MAX_BINDINGS_PER_ACTION }, (_, i) => i);

export function KeybindsTab() {
  const keybinds = settings.value.keybinds;
  const wheelJump = settings.value.controls.jumpOnWheel;
  const [capture, setCapture] = useState<Target | null>(null);
  const [pending, setPending] = useState<PendingConflict | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  /** The slot button that started the capture; gets the focus back after a conflict is resolved. */
  const trigger = useRef<HTMLElement | null>(null);
  const dupes = duplicateBindings(keybinds);

  // Land on the harmless choice: a key that is still repeating must not confirm a replacement.
  useEffect(() => {
    if (pending) strip.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, [pending]);

  const startCapture = (target: Target, source: HTMLElement) => {
    trigger.current = source;
    setPending(null);
    setCapture(target);
  };

  const onCaptured = (binding: Binding) => {
    if (!capture) return;
    setCapture(null);
    const conflicts = findConflicts(settings.value.keybinds, capture.action, binding);
    if (conflicts.length > 0) {
      setPending({ ...capture, binding, conflicts });
      return;
    }
    updateSettings({ keybinds: setBinding(settings.value.keybinds, capture.action, capture.slot, binding) });
  };

  /** `steal`: true replaces the other actions' bindings, false keeps both, null cancels. */
  const resolve = (steal: boolean | null) => {
    if (!pending) return;
    if (steal !== null) {
      updateSettings({
        keybinds: setBinding(settings.value.keybinds, pending.action, pending.slot, pending.binding, steal),
      });
    }
    setPending(null);
    trigger.current?.focus();
  };

  // Escape dismisses the conflict question instead of leaving the screen.
  useEscape(() => resolve(null), pending !== null);

  let status = "Click a slot to rebind it. Each action takes up to two keys, mouse buttons or wheel directions.";
  if (wheelJump) status += " Jump on mouse wheel is on: the wheel presses Jump and its bindings are inactive.";
  if (capture) status = `${CAPTURE_PROMPT} Esc cancels.`;
  if (pending) {
    const used = pending.conflicts.map((a) => ACTION_LABELS[a]).join(", ");
    status = `${bindingLabel(pending.binding)} is already used by ${used}.`;
  }

  return (
    <>
      <div ref={strip} class={cx("settings__keystatus", capture && "is-capturing", pending && "is-warning")}>
        <i class="settings__led" aria-hidden="true" />
        <p class="settings__keystatus-text" role="status">
          {status}
        </p>
        {capture && <Visualizer class="settings__keyviz" bars={8} />}
        {pending && (
          <div class="settings__keystatus-actions">
            <Button size="sm" variant="danger" onClick={() => resolve(true)}>
              Replace
            </Button>
            <Button size="sm" onClick={() => resolve(false)}>
              Keep both
            </Button>
            <Button size="sm" variant="ghost" data-autofocus="" onClick={() => resolve(null)}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {capture && <KeybindCapture onCapture={onCaptured} onCancel={() => setCapture(null)} />}

      <div class="settings__content">
        {ACTION_GROUPS.map((group) => (
          <section key={group.id} class="settings__keygroup" aria-labelledby={`settings-keys-${group.id}`}>
            <h3 id={`settings-keys-${group.id}`} class="settings__group">
              {group.label}
            </h3>
            <div class="settings__rows">
              {group.actions.map((action) => {
                const bindings = keybinds[action];
                const label = ACTION_LABELS[action];
                const dupText = bindings
                  .filter((b) => dupes.has(b))
                  .map((b) => {
                    const others = (dupes.get(b) ?? []).filter((a) => a !== action);
                    return `${bindingLabel(b)} is also bound to ${others.map((a) => ACTION_LABELS[a]).join(", ")}`;
                  })
                  .join("; ");
                const wheelOff = wheelJump && bindings.some((b) => isWheelBinding(b));
                return (
                  <div key={action} class="settings__keyrow" role="group" aria-label={label}>
                    <span class="settings__keylabel">
                      {label}
                      {dupText !== "" && (
                        <span class="settings__dup" title={dupText}>
                          <Badge tone="gold">Duplicate</Badge>
                          <span class="sr-only">{dupText}</span>
                        </span>
                      )}
                      {wheelOff && (
                        <span class="settings__dup" title="Inactive while Jump on mouse wheel is on">
                          <Badge tone="ink">Wheel inactive</Badge>
                        </span>
                      )}
                    </span>
                    {SLOTS.map((slot) => {
                      const target: Target = { action, slot };
                      const binding: Binding | undefined = bindings[slot];
                      const capturing = capture?.action === action && capture.slot === slot;
                      const bound = binding !== undefined;
                      const inactive = wheelJump && bound && isWheelBinding(binding);
                      return (
                        <div key={slot} class="settings__slotwrap">
                          <button
                            type="button"
                            class={cx(
                              "settings__slot",
                              !bound && "is-empty",
                              capturing && "is-capturing",
                              bound && dupes.has(binding) && "is-dup",
                              inactive && "is-inactive",
                            )}
                            aria-label={`${label}, slot ${slot + 1}: ${
                              capturing ? "waiting for input" : bound ? bindingLabel(binding) : "unbound"
                            }${inactive ? ", inactive while Jump on mouse wheel is on" : ""}. Activate to change.`}
                            title={inactive ? "Inactive while Jump on mouse wheel is on" : undefined}
                            data-nav=""
                            onClick={(e) => startCapture(target, e.currentTarget)}
                          >
                            {capturing ? CAPTURE_PROMPT : bound ? bindingLabel(binding) : "Unbound"}
                          </button>
                          <button
                            type="button"
                            class="settings__slotclear"
                            aria-label={`Clear ${label}, slot ${slot + 1}`}
                            disabled={!bound}
                            data-nav=""
                            onClick={() => updateSettings({ keybinds: clearBinding(keybinds, action, slot) })}
                          >
                            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                              <path d="M3 3 13 13M13 3 3 13" stroke="currentColor" stroke-width="2" fill="none" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isActionDefault(keybinds, action)}
                      aria-label={`Reset ${label} to default`}
                      onClick={() => updateSettings({ keybinds: resetAction(keybinds, action) })}
                    >
                      Reset
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
